import { Controller } from "@hotwired/stimulus"
import { trackEvent } from "../analytics"

// Drives the native booking wizard (/book): loads open slots for the chosen
// service/tech/day from the server (which proxies Square), then submits the
// booking. Server endpoints live in BookingsController.
export default class extends Controller {
  static targets = [
    "service", "staffRadio", "date", "slots", "slotsHint",
    "form", "name", "phone", "email", "note", "honeypot",
    "summary", "error", "submitBtn", "wizard", "confirmation",
    "confirmationHeading", "confirmationService", "confirmationDate",
    "confirmationTechnicianRow", "confirmationTechnician",
    "nextOpening", "nextOpeningText",
    "serviceFilter", "serviceFilterEmpty",
    "staffNextOpening"
  ]

  static values = {
    availabilityUrl: String,
    createUrl: String,
    nextUrl: String
  }

  connect() {
    this.selectedSlot = null
    this.selectedSlotKey = null
    this.suggestion = null
    this.idempotencyKey = this.newIdempotencyKey()

    if (this.selectedService) this.loadSlots()
    else this.suggestNextOpening()
  }

  serviceChanged() {
    trackEvent("service_selected", { service: this.selectedService?.name })
    this.loadSlots()
  }

  // Picking a technician only re-filters the slot grid. The per-card "next
  // opening" figures don't depend on which card is selected, so skip refetching
  // them here.
  selectStaff() {
    this.loadSlots({ refreshStaffOpenings: false })
  }

  // The currently chosen technician id ("" = Anyone), read from the checked
  // native radio.
  get selectedStaffId() {
    return this.staffRadioTargets.find((radio) => radio.checked)?.value || ""
  }

  // Hides service options whose name doesn't contain the query. The checked
  // option always stays visible so an active selection can't silently vanish.
  filterServices() {
    const query = this.serviceFilterTarget.value.trim().toLowerCase()
    let visible = 0
    this.serviceTargets.forEach((radio) => {
      // Search the description too, now that it's shown — it lets guests find a
      // service by what it includes ("callus", "paraffin") rather than by a
      // name they'd have to know already.
      const haystack = `${radio.dataset.name || ""} ${radio.dataset.description || ""}`.toLowerCase()
      const match = radio.checked || !query || haystack.includes(query)
      radio.closest("label").hidden = !match
      if (match) visible++
    })
    if (this.hasServiceFilterEmptyTarget) this.serviceFilterEmptyTarget.classList.toggle("hidden", visible > 0)
  }

  get selectedService() {
    const radio = this.serviceTargets.find((el) => el.checked)
    if (!radio) return null
    return { id: radio.value, version: radio.dataset.version, name: radio.dataset.name }
  }

  async loadSlots({ refreshStaffOpenings = true } = {}) {
    const service = this.selectedService
    this.selectSlot(null)
    this.toggleNextOpening(false)
    if (!service) {
      this.slotsHintTarget.textContent = "Select a service above to see open times."
      this.slotsTarget.innerHTML = ""
      this.resetStaffNextOpenings()
      return
    }

    this.slotsHintTarget.textContent = "Checking open times…"
    this.slotsTarget.innerHTML = ""
    if (refreshStaffOpenings) this.updateStaffNextOpenings(service)

    const params = new URLSearchParams({
      service_id: service.id,
      service_version: service.version || "",
      date: this.dateTarget.value
    })
    if (this.selectedStaffId) params.set("team_member_id", this.selectedStaffId)

    try {
      const response = await fetch(`${this.availabilityUrlValue}?${params}`, {
        headers: { Accept: "application/json" }
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Couldn't load times")
      this.renderSlots(data.slots)
    } catch (error) {
      this.slotsHintTarget.textContent = `${error.message}. Please try another day or call us.`
    }
  }

  renderSlots(slots) {
    this.slotsTarget.innerHTML = ""
    if (!slots || slots.length === 0) {
      this.slotsHintTarget.textContent = "No open times that day — try another day or technician."
      this.suggestNextDay()
      return
    }

    this.slotsHintTarget.textContent = "Pick a time:"
    slots.forEach((slot) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "rounded-full border border-cream-200 px-4 py-2 text-sm text-warm-gray-700 transition-colors hover:border-terracotta-400 data-selected:bg-terracotta-500 data-selected:text-cream-50 data-selected:border-terracotta-500"
      button.textContent = this.formatTime(slot.start_at)
      button.addEventListener("click", () => this.selectSlot(slot, button))
      this.slotsTarget.appendChild(button)
    })
  }

  selectSlot(slot, button = null) {
    const slotKey = slot ? [slot.start_at, slot.team_member_id, slot.service_variation_version].join("|") : null
    if (slotKey !== this.selectedSlotKey) {
      this.idempotencyKey = this.newIdempotencyKey()
      this.selectedSlotKey = slotKey
      if (slot) trackEvent("slot_selected", { service: this.selectedService?.name })
    }
    this.selectedSlot = slot
    this.slotsTarget.querySelectorAll("button").forEach((el) => delete el.dataset.selected)
    if (button) button.dataset.selected = "true"
    this.updateSummary()
  }

  updateSummary() {
    const service = this.selectedService
    if (service && this.selectedSlot) {
      this.summaryTarget.textContent =
        `Booking: ${service.name} · ${this.formatDay(this.selectedSlot.start_at)} at ${this.formatTime(this.selectedSlot.start_at)}`
      this.submitBtnTarget.disabled = false
    } else {
      this.summaryTarget.textContent = ""
      this.submitBtnTarget.disabled = true
    }
  }

  async submit(event) {
    event.preventDefault()
    const service = this.selectedService
    if (!service || !this.selectedSlot) return

    this.hideError()
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "Booking…"

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          service_id: service.id,
          service_version: service.version,
          start_at: this.selectedSlot.start_at,
          team_member_id: this.selectedSlot.team_member_id,
          idempotency_key: this.idempotencyKey,
          name: this.nameTarget.value,
          phone: this.phoneTarget.value,
          email: this.emailTarget.value,
          note: this.noteTarget.value,
          website: this.hasHoneypotTarget ? this.honeypotTarget.value : ""
        })
      })
      const data = await response.json()
      if (!response.ok || !data.ok) throw new Error(data.error || "Something went wrong")
      if (!data.booking?.start_at) throw new Error("Your booking may have been created, but confirmation details are unavailable. Please call us before trying again.")

      this.confirmationServiceTarget.textContent = service.name
      this.confirmationDateTarget.textContent =
        `${this.formatDay(data.booking.start_at)} at ${this.formatTime(data.booking.start_at)}`

      const technician = this.technicianNameFor(this.selectedSlot)
      this.confirmationTechnicianTarget.textContent = technician
      this.confirmationTechnicianRowTarget.classList.toggle("hidden", !technician)

      this.formTarget.reset()
      this.serviceTargets.forEach((radio) => (radio.checked = false))
      this.selectedSlot = null
      this.selectedSlotKey = null
      this.idempotencyKey = this.newIdempotencyKey()
      this.wizardTarget.classList.add("hidden")
      this.wizardTarget.setAttribute("aria-hidden", "true")
      this.confirmationTarget.classList.remove("hidden")
      this.confirmationTarget.removeAttribute("aria-hidden")
      this.confirmationTarget.scrollIntoView({ behavior: "smooth", block: "center" })
      window.requestAnimationFrame(() => this.confirmationHeadingTarget.focus({ preventScroll: true }))
    } catch (error) {
      this.showError(`${error.message}. The time may have just been taken — refresh the times and try again, or call us.`)
      this.submitBtnTarget.disabled = false
    } finally {
      this.submitBtnTarget.textContent = "Confirm booking"
    }
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorTarget.classList.add("hidden")
  }

  // Fetches the soonest opening (next 14 days, first catalog service) so a
  // visitor who hasn't picked anything yet sees a concrete time to jump to.
  // Best-effort: any failure just leaves the banner hidden.
  async suggestNextOpening() {
    if (!this.hasNextOpeningTarget || !this.nextUrlValue) return
    const radio = this.serviceTargets[0]
    if (!radio) return
    const service = { id: radio.value, version: radio.dataset.version, name: radio.dataset.name }

    const slot = await this.fetchNextSlot(service.id)
    if (!slot || this.selectedService) return

    this.suggestion = { service, slot }
    this.nextOpeningTextTarget.textContent =
      `${service.name} — ${this.formatDay(slot.start_at)} at ${this.formatTime(slot.start_at)}`
    this.toggleNextOpening(true)
  }

  useNextOpening() {
    if (!this.suggestion) return
    const { service, slot } = this.suggestion
    const radio = this.serviceTargets.find((el) => el.value === service.id)
    if (radio && !radio.checked) {
      radio.checked = true
      trackEvent("service_selected", { service: service.name })
    }
    this.dateTarget.value = this.isoDate(slot.start_at)
    this.loadSlots()
    this.slotsHintTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // When the chosen day has no slots, points at the next day that does with a
  // one-click jump, instead of leaving the visitor to guess dates.
  async suggestNextDay() {
    const service = this.selectedService
    if (!service || !this.nextUrlValue) return
    const fromDate = this.dateTarget.value

    const slot = await this.fetchNextSlot(service.id, fromDate)
    if (!slot) return
    // Skip if stale (the visitor changed inputs mid-fetch) or same-day.
    if (this.selectedService?.id !== service.id || this.dateTarget.value !== fromDate) return
    if (this.isoDate(slot.start_at) === fromDate) return

    const button = document.createElement("button")
    button.type = "button"
    button.className = "rounded-full border border-terracotta-400 px-4 py-2 text-sm font-medium text-terracotta-700 transition-colors hover:bg-terracotta-500 hover:text-cream-50"
    button.textContent = `Next opening: ${this.formatDay(slot.start_at)} at ${this.formatTime(slot.start_at)} →`
    button.addEventListener("click", () => {
      this.dateTarget.value = this.isoDate(slot.start_at)
      this.loadSlots()
    })
    this.slotsTarget.appendChild(button)
  }

  async fetchNextSlot(serviceId, fromDate = null) {
    const data = await this.fetchNextAvailability(serviceId, fromDate)
    if (!data) return null

    const teamId = this.selectedStaffId
    if (teamId) return (data.technicians || []).find((tech) => tech.id === teamId)?.next_slot || null
    return data.anyone_next_slot || null
  }

  async fetchNextAvailability(serviceId, fromDate = null) {
    const params = new URLSearchParams({ service_id: serviceId, days: "14" })
    if (fromDate) params.set("date", fromDate)

    try {
      const response = await fetch(`${this.nextUrlValue}?${params}`, { headers: { Accept: "application/json" } })
      const data = await response.json()
      if (!response.ok) return null
      return data
    } catch {
      return null
    }
  }

  async updateStaffNextOpenings(service = this.selectedService) {
    if (!this.hasStaffNextOpeningTarget || !service || !this.nextUrlValue) return
    const date = this.dateTarget.value

    this.staffNextOpeningTargets.forEach((target) => {
      target.textContent = "Checking next opening…"
    })

    const data = await this.fetchNextAvailability(service.id, date)
    if (this.selectedService?.id !== service.id || this.dateTarget.value !== date) return
    if (!data) {
      this.staffNextOpeningTargets.forEach((target) => {
        target.textContent = "Openings unavailable — choose a day below"
      })
      return
    }

    const byTechnician = new Map((data.technicians || []).map((tech) => [tech.id, tech.next_slot]))
    this.staffNextOpeningTargets.forEach((target) => {
      const staffId = target.dataset.staffId || ""
      const slot = staffId ? byTechnician.get(staffId) : data.anyone_next_slot
      target.textContent = slot
        ? `Next opening: ${this.formatDay(slot.start_at)} at ${this.formatTime(slot.start_at)}`
        : "No opening in the next 14 days"
    })
  }

  resetStaffNextOpenings() {
    if (!this.hasStaffNextOpeningTarget) return
    this.staffNextOpeningTargets.forEach((target) => {
      target.textContent = "Select a service to check openings"
    })
  }

  toggleNextOpening(show) {
    if (!this.hasNextOpeningTarget) return
    this.nextOpeningTarget.classList.toggle("hidden", !show)
    this.nextOpeningTarget.classList.toggle("flex", show)
  }

  isoDate(iso) {
    const date = new Date(iso)
    const pad = (part) => String(part).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }

  technicianNameFor(slot) {
    if (!slot?.team_member_id) return ""

    const radio = this.staffRadioTargets.find((candidate) => candidate.value === slot.team_member_id)
    return radio?.dataset.staffName?.trim() || ""
  }

  newIdempotencyKey() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`
  }

  formatTime(iso) {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
  }

  formatDay(iso) {
    return new Date(iso).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })
  }
}
